<template>
  <div class="shipments relative full-page-width ex-small-width">
    <!-- INFO BOX -->
    <message v-if="draftError" style="margin-bottom: 25px" name="error" :data="data={data:draftError, type:'validation'}"></message>

    <!-- CARD FROM -->
    <div class="text-box">
      <h2 class="m-b-20 section-title">{{ $t('Where do you ship from') }}</h2>
      <div class="row">
        <div class="col-xs-12">
          <div class="card">
            <div>
              <div class="row m-b-input">
                <div class="col-xs-6">
                  <Calendar
                    :label="$t('ship_date')"
                    v-model="shippingDate"
                    :date="shippingDate || new Date()"
                    :rules="{required: true}" />
                </div>
                <div class="col-xs-6">
                  <InputText
                    v-cloak
                    v-model="fromData.reference"
                    :label="$t('reference')"
                    :rules="{required: isSenderReferenceRequired}"
                  />
                </div>
              </div>
              <InputSelect
                :class="foreignShipper || showCustoms ? 'm-b-input' : ''"
                :label="$t('Shipper')"
                v-model="shipper"
                @change="checkForForeign"
                :countryFlag="true"
                :options="shipperOptions"
              />
              <AddressPicker
                class="m-b-input"
                v-if="foreignShipper"
                :label="$t('address_1')"
                placeholder="Search address"
                @addressSelected="changeAddress('from', $event)"
                v-model="fromAddress"
              />
              <div class="row m-b-input" v-if="foreignShipper">
                <div :class="foreignShipper ? 'col-xs-4' : 'col-xs-12'" v-if="showCustoms && foreignShipper">
                  <InputText
                    v-model="fromData.vat"
                    label="EORI"
                    :rules="{required: false}"
                  />
                </div>
                <div :class="!showCustoms ? 'col-xs-6' : 'col-xs-4'" v-if="foreignShipper">
                  <InputText
                    v-model="fromData.company"
                    :label="$t('company')"
                  />
                </div>
                <div  :class="!showCustoms ? 'col-xs-6' : 'col-xs-4'" v-if="foreignShipper">
                  <InputText
                    v-model="fromData.attention"
                    :label="$t('att.')"
                    :rules="{required: true}"
                  />
                </div>
              </div>
              <div class="row" v-if="foreignShipper" :class="{'m-b-input' : showCustoms}">
                <div class="col-xs-6">
                  <PhoneSelect key="fromPhone" v-model="fromData.phone" :rules="{required: isPhoneRequired}"/>
                </div>
                <div class="col-xs-6">
                  <InputText
                    v-model="fromData.email"
                    label="Email"
                    :rules="{required: isEmailRequired, email: isEmailRequired}"
                  />
                </div>
              </div>

            </div>
            <div class="separator" v-if="show_other_shipper_address"></div>
            <!-- WAYBILL BOX -->
            <div v-if="show_other_shipper_address">
              <div :class="toWaybill ? 'm-b-35' : ''">
                <Toggle
                  class="m-r-10"
                  id="toWaybill"
                  :label="$t('Show other shipper information on waybill')"
                  v-model="toWaybill"
                />
                {{ $t('Show other shipper information on waybill') }}
              </div>
              <div class="m-b-40" v-if="toWaybill">
                <InputSelect
                  class="m-b-20"
                  :label="$t('Waybill information')"
                  v-model="waybillShipper"
                  @change="changeWaybillAddressShipper"
                  :options="shipperOptions"
                />
                <AddressPicker
                  class="m-b-20"
                  v-if="toWaybill && (waybillShipper === 'custom')"
                  :label="$t('address_1')"
                  @addressSelected="changeAddress('waybill', $event)"
                  v-model="waybillAddress"
                  placeholder="Search address"
                />
                <div class="row m-b-20" v-if="toWaybill && (waybillShipper === 'custom')">
                  <div class="col-xs-6">
                    <InputText
                      v-model="waybillData.company"
                      :label="$t('company')"
                    />
                  </div>
                  <div class="col-xs-6">
                    <InputText
                      v-model="waybillData.attention"
                      :label="$t('att.')"
                      :rules="{required: true}"
                    />
                  </div>
                </div>
                <div class="row" v-if="toWaybill && (waybillShipper === 'custom')">
                  <div class="col-xs-6">
                    <PhoneSelect v-model="waybillData.phone" :rules="{required: isPhoneRequired}"/>
                  </div>
                  <div class="col-xs-6">
                    <InputText
                      v-model="waybillData.email"
                      label="Email"
                      :rules="{required: isEmailRequired}"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="separator"></div>
            <div>
              <div class="clear-float">
                <Btn
                  @click.native="swapData"
                  class="gray float-left m-r-20"
                  :icon="{
                    name: 'swapVertical',
                    size: '18',
                  }"
                  :btnText="$t('swap_from_to')"
                />
                <Btn
                  @click.native="clearFromFields"
                  class="gray float-left m-r-20"
                  :icon="{
                    name: 'deleteForever',
                    size: '18',
                  }"
                  :btnText="$t('Clear all')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="separator"></div>
    <!-- CARD TO -->
    <div class="text-box">
      <h2 class="m-b-20 section-title">{{ $t('Where do you ship to') }}</h2>
      <div class="row">
        <div class="col-xs-12">
          <div class="card">
            <div>
              <div class="m-b-input">
                <Toggle
                  class="m-r-10"
                  :label="$t('ship_to_private')"
                  v-model="toData.shipToPrivateResident"
                /> {{ $t('ship_to_private') }}
              </div>

              <AddressPicker
                class="m-b-input"
                :label="$t('address_1')"
                placeholder="Search address"
                @addressSelected="changeAddress('to', $event)"
                v-model="toAddress"
              />
              <div class="row m-b-input">
                <div class="col-xs-3" v-if="showCustoms && !toData.shipToPrivateResident">
                  <InputText
                    v-model="toData.vat"
                    label="EORI"
                    :rules="{required: false}"
                  />
                </div>
                <div :class="showCustoms ? 'col-xs-3' : 'col-xs-4'" v-if="!toData.shipToPrivateResident">
                  <InputText
                    v-model="toData.company"
                    :label="$t('company')"
                    :rules="{required: true }"
                  />
                </div>
                <div :class="(!toData.shipToPrivateResident && showCustoms) ? 'col-xs-3' : !toData.shipToPrivateResident ? 'col-xs-4' : 'col-xs-6'">
                  <InputText
                    v-model="toData.attention"
                    :label="$t('att.')"
                    :rules="{required: !! toData.shipToPrivateResident}"
                  />
                </div>
                <div :class="(!toData.shipToPrivateResident && showCustoms) ? 'col-xs-3' : !toData.shipToPrivateResident ? 'col-xs-4' : 'col-xs-6'">
                  <InputText
                    v-model="toData.reference"
                    :label="$t('reference')"
                    :rules="{required: isReceiverReferenceRequired}"
                  />
                </div>
              </div>
              <div class="row">
                <div class="col-xs-6">
                  <PhoneSelect key="toPhone" v-model="toData.phone" :rules="{required:isPhoneRequired}"/>
                </div>
                <div class="col-xs-6">
                  <InputText
                    v-model="toData.email"
                    label="Email"
                    :rules="{required: isEmailRequired, email: isEmailRequired}"
                  />
                </div>
              </div>
              <div class="separator"></div>
              <div class="clear-float">
                <Btn
                  @click.native="swapData"
                  class="gray float-left m-r-20"
                  :icon="{
                    name: 'swapVertical',
                    size: '18',
                  }"
                  :btnText="$t('swap_from_to')"
                />
                <Btn
                  @click.native="clearAddressData('to')"
                  class="gray float-left m-r-20"
                  :icon="{
                    name: 'deleteForever',
                    size: '18',
                  }"
                  :btnText="$t('Clear all')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="separator"></div>
    <!-- GOODS -->
    <div class="text-box">
      <div class="row" v-for="(item, index) in shipmentPackages" :key="`${item.goodsTypeName}-${index}`">
        <div v-if="item.goodsType === 2 && get_parcel_types != null" class="col-xs-12">
          <DocumentBlock
            :label="`#${index+1} - ${$t(item.goodsTypeName)}`"
            v-model="shipmentPackages[index]"
            :removable="shipmentPackages.length > 1"
            :showColliAmount="showColliAmount"
            :customId="`palletID-${index}`"
            @removeDocument="removePackage(index)"
            @activePackageGoodsType="updateGoodsType($event, index)"/>
        </div>
        <div v-if="item.goodsType === 1 && get_parcel_types != null" class="col-xs-12">
          <ParcelBlock
            v-model="shipmentPackages[index]"
            :showTemperature="show_has_temperature"
            :showDangerousGoods="showDangerousGoods"
            :showOnlyDescription="false"
            :isCustomsInvoiceUploaded="!!invoiceFile"
            :customId="`parcelID-${index}`"
            :label="`#${index+1} - ${$t(item.goodsTypeName)}`"
            :removable="shipmentPackages.length > 1"
            :showOrigin="showOrigin"
            :showCustoms="showCustoms"
            :showColliContent="showColliContent[index] ? showColliContent[index] : false"
            :showColliAmount="showColliAmount"
            :isShipmentInEdit="get_shipment_editing_mode"
            :dgTypeShownOnPackageCustomId="dgTypeShownOnPackageCustomId"
            @removeParcel="removePackage(index)"
            @activePackageGoodsType="updateGoodsType($event, index)"
            @packagingTypeChange="packagingTypeChange(index)"
            @packagingTypeOptionChange="packagingTypeOptionChange(index)"
            @dgTypeChanged="dgTypeChanged($event, index)"
          />
        </div>
        <div v-if="item.goodsType === 3 && get_parcel_types != null" class="col-xs-12">
          <PalleteBlock
            v-model="shipmentPackages[index]"
            :showTemperature="show_has_temperature"
            :showDangerousGoods="showDangerousGoods"
            :isCustomsInvoiceUploaded="!!invoiceFile"
            :showOnlyDescription="false"
            :label="`#${index+1} - ${$t(item.goodsTypeName)}`"
            :customId="`palletID-${index}`"
            :removable="shipmentPackages.length > 1"
            :showOrigin="showOrigin"
            :showCustoms="showCustoms"
            :showColliContent="showColliContent[index] ? showColliContent[index] : false"
            :showColliAmount="showColliAmount"
            :dgTypeShownOnPackageCustomId="dgTypeShownOnPackageCustomId"
            @removePallet="removePackage(index)"
            @activePackageGoodsType="updateGoodsType($event, index)"
            @packagingTypeChange="packagingTypeChange(index)"
            @packagingTypeOptionChange="packagingTypeOptionChange(index)"
            @dgTypeChanged="dgTypeChanged($event, index)"
          />
        </div>
        <div v-if="item.goodsType === 4  && get_parcel_types != null" class="col-xs-12">
          <LoadBlock
            v-model="shipmentPackages[index]"
            :showTemperature="show_has_temperature"
            :showDangerousGoods="showDangerousGoods"
            :isCustomsInvoiceUploaded="!!invoiceFile"
            :showOnlyDescription="false"
            :customId="`loadID-${index}`"
            :label="`#${index+1} - ${$t(item.goodsTypeName)}`"
            :showOrigin="showOrigin"
            :showColliContent="showColliContent[index] ? showColliContent[index] : false"
            :showColliAmount="showColliAmount"
            :showCustoms="showCustoms"
            :removable="shipmentPackages.length > 1"
            :dgTypeShownOnPackageCustomId="dgTypeShownOnPackageCustomId"
            @removeLoad="removePackage(index)"
            @activePackageGoodsType="updateGoodsType($event, index)"
            @packagingTypeChange="packagingTypeChange(index)"
            @packagingTypeOptionChange="packagingTypeOptionChange(index)"
            @dgTypeChanged="dgTypeChanged($event, index)"
          />
        </div>
      </div>
      <div>
        <Btn
          @click.native="addContentItem"
          class="brand text-center"
          :icon="{
              name: 'plus',
              size: '18',
            }"
          :btnText="`${$t('add')}`"
        />
      </div>
    </div>
    <div class="separator"></div>
    <!-- CUSTOM INFORMATION -->
    <div v-if="showCustoms" class="text-box">
      <h2 class="m-b-20 section-title">{{ $t('How do you provide custom information?') }}</h2>
      <div class="row">
        <div class="col-xs-12">
          <div class="card">
            <div>
              <div class="row m-b-20">
                <div class="col-xs-6">
                  <div
                    :class="isActiveCustom ? 'active brand' : ''"
                    class="button-bar"
                    @click="customInfo">
                    <Icon
                      class="m-r-20"
                      name="document"
                      :circle="true"
                      color="#017A75"
                      size="20"/>
                    <div class="text-box">
                      <span class="main-text">{{ $t('System generated custom invoice') }}</span>
                    </div>
                  </div>
                </div>
                <div class="col-xs-6">
                  <div
                    :class="!isActiveCustom ? 'active brand' : ''"
                    class="button-bar"
                    @click="addFile">
                    <Icon
                      class="m-r-20"
                      name="cloudUpload"
                      :circle="true"
                      size="20"/>
                    <div class="text-box">
                      <input
                        @change="newFile"
                        type="file"
                        ref="invButton"
                        style="display:none"/>
                      <span class="main-text">{{ $t('Upload your own PDF customs invoice') }}</span>
                      <span class="sub-title" v-if="invoiceFile !== null">{{ invoiceFileName }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="separator medium"></div>
              <div class="row">
                <div class="col-xs-6" :class="infoItemsClass">
                  <InputSelect
                    :label="$t('Invoice type')"
                    v-model="invoiceType"
                    :options="invoiceTypeOptions"
                  />
                </div>
                <div class="col-xs-6" :class="infoItemsClass">
                  <InputSelect
                    :multiSelect=true
                    :log="true"
                    :preselected="selectedInfoItems"
                    :label="$t('Additional info')"
                    @change="selectAdditionalInfo"
                    :options="additionalInfoOptions" />
                </div>
              </div>
              <div class="row">
                <InputText
                  v-if="selectedInfoItems.indexOf('2') > -1"
                  :class="infoItemsClassStretch"
                  :label="$t('ECCN number')"
                  v-model="eccn" />
              </div>
              <div class="row m-t-10">
                <InputText
                    v-if="selectedInfoItems.indexOf('3') > -1"
                    :class="infoItemsClassStretch"
                    :label="$t('EEI number')"
                    v-model="eeiitn.eei" />
              </div>
              <div class="row m-t-10">
                <InputText
                    v-if="selectedInfoItems.indexOf('3') > -1"
                    :class="infoItemsClassStretch"
                    :label="$t('Tin number')"
                    v-model="eeiitn.tinNumber" />
              </div>
              <div class="row m-t-10">
                <InputSelect
                    v-if="selectedInfoItems.indexOf('3') > -1"
                    :class="infoItemsClassStretch"
                    :label="$t('Tin type')"
                    v-model="eeiitn.tinType"
                    :options="tinTypeOptions"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="separator" v-if="showCustoms"></div>
    <!-- PAYMENT TERMS -->
    <PaymentTerms
      v-if="show_freight_paid_by || show_incoterms"
      v-model="customsData"
      @incotermsServicesUpdate="updateIncotermsServices"
      :showFreightBy="show_freight_paid_by"
      :showIncoterms="show_incoterms"
      :showCustoms="showCustoms"
    />
    <div class="separator" v-if="custom_tag_json.length > 0 && (show_freight_paid_by || show_incoterms)"></div>
    <!-- TAGS -->
    <div class="text-box" v-if="custom_tag_json.length > 0">
      <h2 class="m-b-20 section-title">{{ $t('Custom tags') }}</h2>
      <div class="card">
        <div class="row">
          <InputText
            class="m-b-20"
            :class="colSize"
            v-for="(tag, index) in custom_tag_json" :key="`tag-${index}`"
            :label="tag.custom_tag_name"
            :rules="{required: tag.custom_tag_is_required}"
            v-model="custom_tag_json[index].custom_tag_value" />
        </div>
      </div>
    </div>
    <!-- ADDITIONAL SERVICES -->
    <div class="separator"></div>
    <message style="margin-bottom: 25px"  name="warning" v-if="getShowInfoBox()" :data="data={data:$t('confirm_selection_of_account'), type:'validation'}">
      <SubaccountSelect
        style="display: flex; flex-grow: 1; margin-left: 10px; align-self: center; margin: 0 0 0 20px;"
        :show-search="false"
        :default-text="$t('select_account')"
        :multiSelect="false"
        v-model="bookingSubaccount"
        @change="setBookingSubaccount"
        class="m-b-20"
      />
    </message>
    <div class="separator" v-if="getShowInfoBox()"></div>
    <message v-if="!isServicesBlockActive" name="warning" :data="data={data:isServicesActiveMessage, type:'validation'}"></message>
    <div class="text-box" v-show="isServicesBlockActive && (!getShowInfoBox() || (getShowInfoBox() && bookingSubaccount))">
      <AdditionalServices
        :shipperAsSenderID="shipper != null ? shipper : '0'"
        :shippingDate="shippingDate"
        :pickupDate="shippingDate"
        :selectedProductId="selectedProduct"
        :editShipmentDroppointNo="editShipmentDroppointNo"
        :apiFeeIds="feeIdsJson"
        :apiActiveServices="active_services_json"
        :additionalServicesFieldsData="additionalServicesFieldsData"
        :observableObject="observableObject"
        :dataForProducts="dataForProducts"
        :fromReferenceListener="{
          ref: fromData.reference,
          required: isSenderReferenceRequired,
        }"
        :toReferenceListener="{
          ref: toData.reference,
          required: isReceiverReferenceRequired,
        }"
        :customsData="customsData"
        :companyFromListener="fromData.company"
        :companyToListener="toData.company"
        :shipToPrivateResident="toData.shipToPrivateResident"
        @bookShipment="bookShipment"
        :showColliContent="showColliContent"
        :incotermServiceIds="incotermServices"
        :pickupTimeFrom="pickupTimeFrom"
        :pickupTimeUntil="pickupTimeUntil"
        :pickupDriverNote="pickupDriverNote"
      />
    </div>
    <div class="m-b-40"></div>
    <Popup
      v-if="showShipmentPopup"
      :showConfirmButton=false
      :showPrintButton=showBookingPrintButton
      :showPreviewButton=showViewShipmentButton
      :showDownloadButton=showDownloadShippingLabelsButton
      cancelText="close"
      downloadText="Download waybills"
      @print="printShipment"
      @download="downloadShipment"
      @redirect="redirectFromShipment"
      @cancel="closeShipmentPopup">
      <template v-slot:header>
        <h2>{{ getWaybillText }}</h2>
      </template>
      <div>
        <div class="text-center" v-if="!isPDFGenerated">
          <Icon
            class="icon"
            name="animation"
            size="64" />
          <h2 class="m-b-30">{{ $t('Generating waybills') }}</h2>
        </div>
        <div v-if="isPDFGenerated">
          <pdf
            :numPages="numPages"
            :src="pdfFilePath"
            :rotate="getRotationDegree"
          ></pdf>
        </div>
        <iframe
          style="display: none;"
          ref="printPDF"
          id="printPDF"
          name="printPDF"
          width="100%"
          height="600px" />
        <a
          style="display: none"
          :download="pdfName"
          ref="downloadPDF"
          :href="pdfPath" />
      </div>
    </Popup>
    <Popup
      v-if="showUnapprovedPopup"
      :showConfirmButton=false
      @cancel="closeUnapprovedPopup"
      cancelText="close"
    >
      <template v-slot:header>
        <h2>{{ $t('Shipment waiting for approval') }}</h2>
      </template>
      <div class="popup-info-text">
          {{ $t('Shipment_approval_text') }}
      </div>
    </Popup>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from 'vuex';
import pdf from '@/components/global/pdfWrapper/index';
import mixin from '@/mixins/mixins.js';
import shipmentMixin from '@/mixins/shipment.js';
import Btn from '@/components/elements/buttons';
import AddressPicker from '@/components/global/addressPicker/index.vue';
import InputText from '@/components/elements/inputs/text.vue';
import Calendar from '@/components/elements/inputs/calendar.vue';
import InputSelect from '@/components/elements/inputs/select.vue';
import PhoneSelect from '@/components/elements/inputs/phone.vue';
import Toggle from '@/components/elements/inputs/toggle.vue';
import Popup from '@/components/global/popup';

import DocumentBlock from '@/components/shipments/documentBlock.vue';
import ParcelBlock from '@/components/shipments/parcelBlock.vue';
import PalleteBlock from '@/components/shipments/palleteBlock.vue';
import LoadBlock from '@/components/shipments/loadBlock.vue';
import AdditionalServices from '@/components/shipments/services.vue';
import PaymentTerms from '@/components/shipments/paymentTerms.vue';

import stateList from '@/data/state.js';
import countryList from "@/data/country";
import i18n from "@/i18n";
import SubaccountSelect from "@/components/elements/inputs/subaccountSelect.vue";

function initialState() {
  return {
    shipmentTraceNo: '',
    pickupDriverNote: '',
    pickupTimeFrom: '',
    pickupTimeUntil: '',
    draftError: null,
    // Globals
    dgType: null,
    dgTypeShownOnPackageCustomId: '',
    dangerousGoodsLoaded: false,
    bookingSubaccount: null,
    showColliAmount: false,
    showColliContent: [],
    systemShowColliContent: false,
    showInfoBox: false,
    isReturn: false,
    editShipmentDroppointNo: '',
    shippingAgentName: '',
    showUnapprovedPopup: false,
    draftShippmentId: '',
    isSenderReferenceRequired: false,
    isReceiverReferenceRequired: false,
    isPhoneRequired: true,
    isEmailRequired: true,
    shippingDate: '',
    foreignShipper: false,
    shipper: '',
    shipperOptions: [],
    swapped: false,
    isShipperInFrom: false,
    cachedShipper: '',
    // SENDER
    fromAddress: {
      address_1: '',
      address_2: '',
      country_id: '',
      post_code: '',
      city: '',
      state: '',
    },
    fromData: {
      reference: '',
      company: '',
      attention: '',
      email: '',
      vat: '',
      phone: {
        number: '',
        country: '',
      },
    },
    toWaybill: false,
    waybillShipper: 'custom',
    waybillAddress: {
      address_1: '',
      address_2: '',
      country_id: '',
      post_code: '',
      city: '',
      state: '',
    },
    waybillData:{
      company: '',
      attention: '',
      email: '',
      phone: {
        number: '',
        country: '',
      },
    },
    // CUSTOMER - TO DATA
    toAddress: {
      address_1: '',
      address_2: '',
      country_id: '',
      post_code: '',
      city: '',
      state: '',
    },
    toData: {
      company: '',
      shipToPrivateResident: false,
      reference: '',
      attention: '',
      email: '',
      vat: '',
      phone: {
        number: '',
        country: '',
      },
    },
    // CUSTOMS INFORMATION
    additionalInfoOptions: [
      {
        text: 'ECCN',
        value: '2',
      },
      {
        text: 'EEI',
        value: '3',
      },
    ],
    selectedInfoItems: [],
    eori: '',
    eccn: '',
    eeiitn: {
      eei: '',
      tinType: '',
      tinNumber: '',
    },
    // UPLOAD BOX
    showCustoms: false,
    isActiveCustom: true,
    invoiceFile: null,
    invoiceFileName: '',
    invoiceType: '2',
    invoiceTypeOptions: [
      { text: `${i18n.t('Sample')}`, value: 1},
      { text: `${i18n.t('Sale')}`, value: 2},
      { text: `${i18n.t('Gift')}`, value: 3},
      { text: `${i18n.t('Return_1')}`, value: 4},
      { text: `${i18n.t('Repair')}`, value: 5},
      { text: `${i18n.t('Temporary export')}`, value: 6},
      { text: `${i18n.t('Exhibition goods')}`, value: 7},
      { text: `${i18n.t('Testing analysis')}`, value: 8},
    ],
    tinTypeOptions: [
      { text: `${i18n.t('BUSINESS_NATIONAL')}`, value: 1},
      { text: `${i18n.t('BUSINESS_STATE')}`, value: 2},
      { text: `${i18n.t('BUSINESS_UNION')}`, value: 3},
      { text: `${i18n.t('PERSONAL_NATIONAL')}`, value: 4},
      { text: `${i18n.t('PERSONAL_STATE')}`, value: 5},
    ],
    // PAYMENT TERMS BOX
    customsData: {
      customsAddress: {
        address_1: '',
        address_2: '',
        country_id: '',
        post_code: '',
        city: '',
        state: '',
      },
      customsAddressData:{
        company: '',
        attention: '',
        email: '',
        reference: '',
        vat: '',
        phone: {
          number: '',
          country: '',
        },
      },
      goods_incoterm: 'DAP',
      duty_paid_by: 1,
      duty_payer_account_no: '',
      freight_paid_by: 1,
      freight_payer_account_no: '',
      freight_payer_id: '',
    },
    // GOODS
    showOrigin: true,
    activeGoodsTypes: [],
    shipmentPackages: [],
    defaultPackageBlocks: {
      parcel: {
        goodsTypeName: 'Parcel',
        goodsType: 1,
        colli: 1,
        description: '',
        parcelType: '',
        packagingName: '',
        colliWeight: {
          value: '',
          unit: 'kg',
        },
        dimensions: {
          width: {
            value: '',
            disabled: false,
          },
          height: {
            value: '',
            disabled: false,
          },
          length: {
            value: '',
            disabled: false,
          },
          dimensionsType: '',
        },
        isTemperature: false,
        minTemperature: '',
        maxTemperature: '',
        content: [
          {
            qty: 1,
            description: '',
            unitWeight: {
              value: '',
              unit: 'kg',
            },
            unitValue: {
              value: '',
              unit: 'dkk',
            },
            hsCode: '',
            origin: '',
          },
        ],
        isDgToggleActive: false,
        qty: 1,
        Q: '',
        packingType: '',
        packingTypeOption: 2,
        isOverpack: 0,
        allPackedInOne: 2,
        iataLimitations: '',
        iataRadioactive: '',
        iataAdditionalInfo: '',
        dangerousGoodsServices: [
          {
            feeWrapperId: null,
            dangerousGoodsId: '',
            nemWeight: {
              unit: 'kg',
              value: null,
            },
            netWeight: {
              unit: 'kg',
              value: null,
            },
            bruttoWeight: {
              unit: 'kg',
              value: null,
            },
            englishTechnicalDesignation: '',
            danishTechnicalDesignation: '',
            iataAutorization: '',
            flashPoint: '',
            segregationGroup: '',
            ems: '',
            notAllInOneQty: '',
            notAllInOneOuterPackaging: '',
            innerPackaging: '',
            innerPackagingQty: '',
            packingInstruction: '',
            isEnvironmentalHazard: false,
            isMarinePollutant: false,
            isLimitedQuantity: false,
            isExceptedQuantity: false
          }
        ]
      },
      document: {
        goodsTypeName: 'Document',
        goodsType: 2,
        colli: 1,
        parcelType: '',
        packagingName: '',
        colliWeight: {
          value: '',
          unit: 'kg',
        },
        dimensions: {
          width: {
            value: '',
            disabled: false,
          },
          height: {
            value: '',
            disabled: false,
          },
          length: {
            value: '',
            disabled: false,
          },
          dimensionsType: '',
        },
      },
      pallet: {
        goodsTypeName: 'Pallet',
        goodsType: 3,
        colli: 1,
        description: '',
        palleteType: 'Euro pallet - 120cm x 80cm',
        colliWeight: {
          value: '',
          unit: 'kg',
        },
        dimensions: {
          width: {
            value: '',
            disabled: false,
          },
          height: {
            value: '',
            disabled: false,
          },
          length: {
            value: '',
            disabled: false,
          },
          dimensionsType: 'cm',
        },
        isTemperature: false,
        minTemperature: '',
        maxTemperature: '',
        pallet_stackable_type: 'stackable',
        palletSpace: '',
        // stackablePallets: true,
        content: [
          {
            qty: 1,
            description: '',
            unitWeight: {
              value: '',
              unit: 'kg',
            },
            unitValue: {
              value: '',
              unit: 'dkk',
            },
            hsCode: '',
            origin: '',
          },
        ],
        isDgToggleActive: false,
        packingType: '',
        packingTypeOption: 2,
        isOverpack: 0,
        allPackedInOne: 2,
        iataRadioactive: '',
        iataLimitations: '',
        iataAdditionalInfo: '',
        dangerousGoodsServices: [
          {
            qty: 1,
            feeWrapperId: null,
            dangerousGoodsId: '',
            nemWeight: {
              unit: 'kg',
              value: null,
            },
            netWeight: {
              unit: 'kg',
              value: null,
            },
            bruttoWeight: {
              unit: 'kg',
              value: null,
            },
            englishTechnicalDesignation: '',
            danishTechnicalDesignation: '',
            iataAutorization: '',
            flashPoint: '',
            segregationGroup: '',
            ems: '',
            notAllInOneQty: '',
            notAllInOneOuterPackaging: '',
            innerPackaging: '',
            innerPackagingQty: '',
            packingInstruction: '',
            isEnvironmentalHazard: false,
            isMarinePollutant: false,
            isLimitedQuantity: false,
            isExceptedQuantity: false
          }
        ]
      },
      load: {
        goodsTypeName: 'Load',
        goodsType: 4,
        colli: 1,
        description: '',
        parcelType: 0, //Unknown packaging
        packagingName: '',
        colliWeight: {
          value: '',
          unit: 'kg',
        },
        dimensions: {
          width: {
            value: '',
            disabled: false,
          },
          height: {
            value: '',
            disabled: false,
          },
          length: {
            value: '',
            disabled: false,
          },
          dimensionsType: '',
        },
        cubicMeter: '',
        loadMeter: '',
        isTemperature: false,
        minTemperature: '',
        maxTemperature: '',
        content: [
          {
            qty: 1,
            description: '',
            unitWeight: {
              value: '',
              unit: 'kg',
            },
            unitValue: {
              value: '',
              unit: 'dkk',
            },
            hsCode: '',
            origin: '',
          },
        ],
        isDgToggleActive: false,
        packingType: '',
        packingTypeOption: 2,
        isOverpack: 0,
        allPackedInOne: 2,
        iataRadioactive: '',
        iataLimitations: '',
        iataAdditionalInfo: '',
        dangerousGoodsServices: [
          {
            qty: 1,
            feeWrapperId: null,
            dangerousGoodsId: '',
            nemWeight: {
              unit: 'kg',
              value: null,
            },
            netWeight: {
              unit: 'kg',
              value: null,
            },
            bruttoWeight: {
              unit: 'kg',
              value: null,
            },
            englishTechnicalDesignation: '',
            danishTechnicalDesignation: '',
            iataAutorization: '',
            flashPoint: '',
            segregationGroup: '',
            ems: '',
            notAllInOneQty: '',
            notAllInOneOuterPackaging: '',
            innerPackaging: '',
            innerPackagingQty: '',
            packingInstruction: '',
            isEnvironmentalHazard: false,
            isMarinePollutant: false,
            isLimitedQuantity: false,
            isExceptedQuantity: false
          }
        ]
      },
    },
    // TAGS
    custom_tag_json: [],
    custom_tag_json_tmp: [],
    // SERVICES AND PRODUCTS
    isServicesActiveMessage: '',
    incotermServices: [],
    feeIdsJson: [],
    active_services_json: {},
    selectedProduct: '',
    additionalServicesFieldsData: null,
    //freight_booking_confirmation_no: '',
    // API SERVICES DATA
    apiFeeIds: [],
    apiActiveServices: null,
    // BOOKING
    pdfName: '',
    pdfFile: '',
    pdfPath: '',
    shipmentPopup: false,
    showShipmentPopup: false,
    shipmentId: '',
    isPDFGenerated: false,
    labelImageSource: '',
    pdfFilePath: '',
    numPages: undefined,
    // Permissions
    show_has_temperature: true,
    show_dangerous_goods: true, //Overridden by mixin setting taken from user, if false do not use dg
    show_other_shipper_address: true,
    show_freight_paid_by: true,
    show_incoterms: true,
  }
}

export default {
  name: 'Shipments',
  mixins: [mixin, shipmentMixin],
  components: {
    SubaccountSelect,
    Popup,
    Btn,
    AddressPicker,
    InputSelect,
    InputText,
    Toggle,
    PhoneSelect,
    Calendar,
    AdditionalServices,
    DocumentBlock,
    ParcelBlock,
    PalleteBlock,
    PaymentTerms,
    LoadBlock,
    pdf,
  },
  data() {
      return initialState()
  },
  computed: {
    ...mapGetters([
      'get_shipments_list',
      'get_shipments_count',
      'get_shippers',
      'get_user',
      'get_pallet_types',
      'get_parcel_types',
      'get_editing_shipment',
      'get_shipment_editing_mode',
      'get_active_subaccount',
      'get_single_shipment',
    ]),
    showBookingPrintButton() {
      return !!this.isPDFGenerated
    },
    showDownloadShippingLabelsButton() {
      return !!this.isPDFGenerated
    },
    showViewShipmentButton() {
      return !!this.isPDFGenerated
    },
    getWaybillText () {
      if (!this.isPDFGenerated) {
        return this.$t('Generating waybills')
      }
      return `${this.$t('TXT_BOOKING_COMPLETED')} - ${this.shipmentTraceNo}`;
    },
    showDangerousGoods () {
      return this.show_dangerous_goods && this.dangerousGoodsLoaded;
    },
    needApproval() {
      if(parseInt(this.get_user.book_permission_type) === 1 && parseInt(this.get_active_subaccount.book_approval_type) === 1) return false;
      return true;
    },
    getRotationDegree () {
      let degree = 0;
      if (this.shippingAgentName === 'Dao365' && (parseInt(this.get_user.label_format) === 2 || parseInt(this.get_user.label_format) === 3)) {
        degree = 90;
      }
      return degree;
    },
    isServicesBlockActive() {
      let result = true;
      this.isServicesActiveMessage = `${this.$t('validation_message_services')}: </br></br>`

      if(this.subaccountId == '') {this.isServicesActiveMessage += `&#x2022; ${this.$t('subaccount_id')}</br>`; result = false};
      if(this.shippingDate == '') {this.isServicesActiveMessage += `&#x2022; ${this.$t('ship_date')}</br>`; result = false};
      // Sender
      if(this.fromAddress.address_1 == '') {this.isServicesActiveMessage += `&#x2022; ${this.$t('sender_address')}</br>`; result = false};
      if(this.fromAddress.country_id == '') {this.isServicesActiveMessage += `&#x2022; ${this.$t('sender_country_id')}</br>`; result = false};
      if(this.fromAddress.post_code == '') {this.isServicesActiveMessage += `&#x2022; ${this.$t('sender_post_code')}</br>`; result = false};
      if(this.fromAddress.city == '') {this.isServicesActiveMessage += `&#x2022; ${this.$t('sender_city')}</br>`; result = false};
      if(this.isSenderReferenceRequired){
        if(this.fromData.reference == '' || this.fromData.reference == null ) {this.isServicesActiveMessage += `&#x2022; ${this.$t('sender_reference')}</br>`; result = false};
      }
      // Customer
      if(this.toAddress.address_1 == '') {this.isServicesActiveMessage += `&#x2022; ${this.$t('receiver_address')}</br>`; result = false};
      if(this.toAddress.country_id == '') {this.isServicesActiveMessage += `&#x2022; ${this.$t('receiver_country_id')}</br>`; result = false};
      if(this.toAddress.post_code == '') {this.isServicesActiveMessage += `&#x2022; ${this.$t('receiver_post_code')}</br>`; result = false};
      if(this.toAddress.city == '') {this.isServicesActiveMessage += `&#x2022; ${this.$t('receiver_city')}</br>`; result = false};
      if(this.isReceiverReferenceRequired){
        if(this.toData.reference == '' || this.toData.reference == null) {this.isServicesActiveMessage += `&#x2022; ${this.$t('receiver_reference')}</br>`; result = false};
      }
      // Goods
      // Parcel

      this.shipmentPackages.forEach((parcel, index) => {

        if(parseInt(parcel.goodsType) === 1) {
          let parcelValidation = true;
          let parcelText = `</br>&#x2022; ${this.$t('colli')} ${index + 1}: `;
          if(parcel.colli == '0') {parcelText += `${this.$t('colli_zero_error')}, `; parcelValidation = false};
          if(parcel.colli == '') {parcelText += `${this.$t('colli_empty_error')}, `; parcelValidation = false};
          // Dimensions
          if(parcel.dimensions.width.value == '') {parcelText += `${this.$t('width')}, `; parcelValidation = false};
          if(parcel.dimensions.length.value == '') {parcelText += `${this.$t('length')}, `; parcelValidation = false};
          if(parcel.dimensions.height.value == '') {parcelText += `${this.$t('height')}, `; parcelValidation = false};
          // Dangerous goods
          if (parcel.isDgToggleActive) {
            parcel.dangerousGoodsServices.forEach((row, indexx) => {
              let rowText = `${this.$t('row')} ${(indexx + 1)}: `;
              let rowValidation = true;
              if (row.bruttoWeight.value == '' || row.bruttoWeight.value == 0) {
                rowText += `${this.$t('brutto_weight')}, `;
                rowValidation = false;
                parcelValidation = false;
              }
              if (row.feeWrapperId == '' || row.feeWrapperId == 0) {
                rowText += `${this.$t('type')}, `;
                rowValidation = false;
                parcelValidation = false;
              }
              if (row.dangerousGoodsId == '' || row.dangerousGoodsId == 0) {
                rowText += `${this.$t('un_number')}, `;
                rowValidation = false;
                parcelValidation = false;
              }

              //@todo maybe we need to improve this logic, there is some dynamic field on dg
              // if (row.qty == '' || row.qty == 0) {
              //   rowText += `${this.$t('quantaty')}, `;
              //   rowValidation = false;
              //   parcelValidation = false;
              // }

              if (!rowValidation) {
                // rowText = rowText.replace(/,\s*$/, '');
                parcelText += rowText;
                result = false;
              }
            })
          }
          // Temperature
          if(parcel.isTemperature){
            if(parcel.minTemperature == '') {parcelText += `${this.$t('min_temperature')} ,`; parcelValidation = false};
            if(parcel.maxTemperature == '') {parcelText += `${this.$t('max_temperature')} ,`; parcelValidation = false};
          }

          if (this.showColliContent[index]) {
            //Content data is required
            parcel.content.forEach((row, indexx) => {
              let rowText = `${this.$t('row')} ${(indexx + 1)}: `;
              let rowValidation = true;
              if(row.unitWeight.value == '' || row.unitWeight.value == 0){
                rowText += `${this.$t('weight')}, `; rowValidation = false; parcelValidation = false;
              };
              if(row.qty == '' || row.qty == 0){
                rowText += `${this.$t('quantaty')}, `; rowValidation = false; parcelValidation = false;
              };
              if(!rowValidation){
                // rowText = rowText.replace(/,\s*$/, '');
                parcelText += rowText;
                result = false;
              }
            })
          } else {
            // COlli weight is required
            if(parcel.colliWeight.value == '' || parcel.colliWeight.value == undefined) {
              parcelText += `${this.$t('weight')}, `; parcelValidation = false
            }
          }

          if(!parcelValidation){
            parcelText = parcelText.replace(/,\s*$/, '');
            this.isServicesActiveMessage += parcelText;
            result = false;
          }
        } else if(parseInt(parcel.goodsType) === 2) {
          let parcelValidation = true;
          let parcelText = `</br>&#x2022; ${this.$t('document')} ${index + 1}: `;
          if(parcel.colli == '0') {parcelText += `${this.$t('colli_zero_error')}, `; parcelValidation = false};
          if(parcel.colli == '') {parcelText += `${this.$t('colli_empty_error')}, `; parcelValidation = false};
          // Dimensions
          if(parcel.dimensions.width.value == '') {parcelText += `${this.$t('width')}, `; parcelValidation = false};
          if(parcel.dimensions.length.value == '') {parcelText += `${this.$t('length')}, `; parcelValidation = false};
          if(parcel.dimensions.height.value == '') {parcelText += `${this.$t('height')}, `; parcelValidation = false};
          // COlli weight
          if(parcel.colliWeight.value == '' || parcel.colliWeight.value == undefined) {parcelText += `${this.$t('weight')}, `; parcelValidation = false};

          if(!parcelValidation){
            parcelText = parcelText.replace(/,\s*$/, '');
            this.isServicesActiveMessage += parcelText;
            result = false;
          }
        } else if(parseInt(parcel.goodsType) === 3) {
          let parcelValidation = true;
          let parcelText = `</br>&#x2022; ${this.$t('colli')} ${index + 1}: `;
          if(parcel.colli == '0') {parcelText += `${this.$t('colli_zero_error')}, `; parcelValidation = false};
          if(parcel.colli == '') {parcelText += `${this.$t('colli_empty_error')}, `; parcelValidation = false};
          // Dimensions
          if(parcel.dimensions.width.value == '') {parcelText += `${this.$t('width')}, `; parcelValidation = false};
          if(parcel.dimensions.length.value == '') {parcelText += `${this.$t('length')}, `; parcelValidation = false};
          if(parcel.dimensions.height.value == '') {parcelText += `${this.$t('height')}, `; parcelValidation = false};
          // Temperature
          if(parcel.isTemperature){
            if(parcel.minTemperature == '') {parcelText += `${this.$t('min_temperature')} ,`; parcelValidation = false};
            if(parcel.maxTemperature == '') {parcelText += `${this.$t('max_temperature')} ,`; parcelValidation = false};
          }

          if (this.showColliContent[index]) {
            //Content data is required
            parcel.content.forEach((row, indexx) => {
              let rowText = `${this.$t('row')} ${(indexx + 1)}: `;
              let rowValidation = true;
              if(row.unitWeight.value == '' || row.unitWeight.value == 0){
                rowText += `${this.$t('weight')}, `; rowValidation = false; parcelValidation = false;
              };
              if(row.qty == '' || row.qty == 0){
                rowText += `${this.$t('quantaty')}, `; rowValidation = false; parcelValidation = false;
              };
              if(!rowValidation){
                // rowText = rowText.replace(/,\s*$/, '');
                parcelText += rowText;
                result = false;
              }
            })
          } else {
            // COlli weight is required
            if(parcel.colliWeight.value == '' || parcel.colliWeight.value == undefined) {
              parcelText += `${this.$t('weight')}, `; parcelValidation = false
            }
          }

          //Dangerous goods
          if(parcel.isDgToggleActive){
            parcel.dangerousGoodsServices.forEach((row, indexx) => {
              let rowText = `${this.$t('row')} ${(indexx + 1)}: `;
              let rowValidation = true;
              if (row.bruttoWeight.value == '' || row.bruttoWeight.value == 0) {
                rowText += `${this.$t('brutto_weight')}, `;
                rowValidation = false;
                parcelValidation = false;
              }
              if (row.feeWrapperId == '' || row.feeWrapperId == 0) {
                rowText += `${this.$t('type')}, `;
                rowValidation = false;
                parcelValidation = false;
              }
              if (row.dangerousGoodsId == '' || row.dangerousGoodsId == 0) {
                rowText += `${this.$t('un_number')}, `;
                rowValidation = false;
                parcelValidation = false;
              }

              if(!rowValidation){
                // rowText = rowText.replace(/,\s*$/, '');
                parcelText += rowText;
                result = false;
              }
            })
          }

          if(!parcelValidation){
            parcelText = parcelText.replace(/,\s*$/, '');
            this.isServicesActiveMessage += parcelText;
            result = false;
          }
        } else if(parseInt(parcel.goodsType) === 4) {
          let parcelValidation = true;
          let parcelText = `</br>&#x2022; Load: `;
          // Dimensions
          if(parcel.dimensions.width.value == '' &&
            parcel.dimensions.length.value == '' &&
            parcel.dimensions.height.value == '' &&
            parcel.cubicMeter == '' &&
            parcel.loadMeter == '') {
            parcelText += 'Enter some of dimensions value, ';
            parcelValidation = false;
          }
          // Temperature
          if(parcel.isTemperature){
            if(parcel.minTemperature == '') {parcelText += `${this.$t('min_temperature')} ,`; parcelValidation = false};
            if(parcel.maxTemperature == '') {parcelText += `${this.$t('max_temperature')} ,`; parcelValidation = false};
          }

          if (this.showColliContent[index]) {
            //Content data is required
            parcel.content.forEach((row, indexx) => {
              let rowText = `${this.$t('row')} ${(indexx + 1)}: `;
              let rowValidation = true;
              if(row.unitWeight.value == '' || row.unitWeight.value == 0){
                rowText += `${this.$t('weight')}, `; rowValidation = false; parcelValidation = false;
              };
              if(row.qty == '' || row.qty == 0){
                rowText += `${this.$t('quantaty')}, `; rowValidation = false; parcelValidation = false;
              };
              if(!rowValidation){
                // rowText = rowText.replace(/,\s*$/, '');
                parcelText += rowText;
                result = false;
              }
            })
          } else {
            // COlli weight is required
            if(parcel.colliWeight.value == '' || parcel.colliWeight.value == undefined) {
              parcelText += `${this.$t('weight')}, `; parcelValidation = false
            }
          }

          //Dangerous goods
          if(parcel.isDgToggleActive){
            parcel.dangerousGoodsServices.forEach((row, indexx) => {
              let rowText = `${this.$t('row')} ${(indexx + 1)}: `;
              let rowValidation = true;
              if (row.bruttoWeight.value == '' || row.bruttoWeight.value == 0) {
                rowText += `${this.$t('brutto_weight')}, `;
                rowValidation = false;
                parcelValidation = false;
              }
              if (row.feeWrapperId == '' || row.feeWrapperId == 0) {
                rowText += `${this.$t('type')}, `;
                rowValidation = false;
                parcelValidation = false;
              }
              if (row.dangerousGoodsId == '' || row.dangerousGoodsId == 0) {
                rowText += `${this.$t('un_number')}, `;
                rowValidation = false;
                parcelValidation = false;
              }

              //@todo maybe we need to improve this logic, there is some dynamic field on dg
              // if (row.qty == '' || row.qty == 0) {
              //   rowText += `${this.$t('quantaty')}, `;
              //   rowValidation = false;
              //   parcelValidation = false;
              // }

              if(!rowValidation){
                // rowText = rowText.replace(/,\s*$/, '');
                parcelText += rowText;
                result = false;
              }
            })
          }

          if(!parcelValidation){
            parcelText = parcelText.replace(/,\s*$/, '');
            this.isServicesActiveMessage += parcelText;
            result = false;
          }
        }

      })

      // ICOTERMS VALIDATION
      // if(this.customsData.goods_incoterm == 'DDP' && parseInt(this.customsData.duty_paid_by) === 2 && this.customsData.duty_payer_account_no == ''){
      //   this.isServicesActiveMessage += `</br>&#x2022; ${this.$t('payer_account_number')}`;
      //   result = false;
      // }
      if(parseInt(this.customsData.duty_paid_by) === 3){
        // if(this.customsData.duty_payer_account_no == ''){
        //   this.isServicesActiveMessage += `</br>&#x2022; ${this.$t('payer_account_number')}`;
        //   result = false;
        // }
        if(this.customsData.customsAddress.address_1 == '') {this.isServicesActiveMessage += `</br>&#x2022; ${this.$t('customs_address')}`; result = false};
        if(this.customsData.customsAddress.country_id == '') {this.isServicesActiveMessage += `</br>&#x2022; ${this.$t('customs_country_id')}`; result = false};
        if(this.customsData.customsAddress.post_code == '') {this.isServicesActiveMessage += `</br>&#x2022; ${this.$t('customs_post_code')}`; result = false};
        if(this.customsData.customsAddress.city == '') {this.isServicesActiveMessage += `</br>&#x2022; ${this.$t('customs_city')}`; result = false};
      }
      // if(parseInt(this.customsData.freight_paid_by) === 2 && this.customsData.freight_payer_account_no == ''){
      //   this.isServicesActiveMessage += `</br>&#x2022; ${this.$t('freight_account_number')}`;
      //   result = false;
      // }

      // END
      this.isServicesActiveMessage = this.isServicesActiveMessage.replace(/,\s*$/, '');
      return result;
    },
    colSize() {
      if(this.custom_tag_json.length == 1){
        return 'col-xs-12';
      } else if(this.custom_tag_json.length == 2){
        return 'col-xs-6';
      } else if(this.custom_tag_json.length == 3){
        return 'col-xs-4';
      } else {
        return 'col-xs-3';
      }
    },
    observableObject() {
      return {
        subaccountId: this.bookingSubaccount ? this.bookingSubaccount : this.get_active_subaccount.id,
        shippingDate: this.shippingDate,
        fromAddress: this.fromAddress.address_1,
        fromCountry: this.fromAddress.country_id,
        fromPostcode: this.fromAddress.post_code,
        fromCity: this.fromAddress.city,
        toAddress: this.toAddress.address_1,
        toCountry: this.toAddress.country_id,
        toPostcode: this.toAddress.post_code,
        toCity: this.toAddress.city,
        activeGoodsTypes: this.activeGoodsTypes,
        colli: this.parseColliObservable(),
        goodsIncoterms: this.customsData.goods_incoterm,
        showCustoms: this.showCustoms,
        senderReference: this.fromData.reference,
        reciverReference: this.toData.reference,
      };
    },
    dataForProducts() {
      return {
        subaccountId: this.bookingSubaccount ? this.bookingSubaccount : this.get_active_subaccount.id,
        shippingDate: this.shippingDate,
        fromAddress: this.fromAddress,
        fromData: this.fromData,
        toAddress: this.toAddress,
        toData: this.toData,
        activeGoodsTypes: this.activeGoodsTypes,
        colli: this.parseColli(),
        customsData: this.customsData,
        showCustoms: this.showCustoms,
        isSenderReferenceRequired: this.isSenderReferenceRequired,
        isReceiverReferenceRequired: this.isReceiverReferenceRequired,
        isPhoneRequired: this.isPhoneRequired,
        isEmailRequired: this.isEmailRequired,
      };
    },
    infoItemsClass() {
      if(this.selectedInfoItems.length > 0) return 'm-b-20';
      return '';
    },
    infoItemsClassStretch() {
      if(this.selectedInfoItems.length == 1) return 'col-xs-12';
      if(this.selectedInfoItems.length == 2) return 'col-xs-6';
      return '';
    },
  },
  methods: {
    setBookingSubaccount(subaccountId) {
      this.bookingSubaccount = subaccountId
      this.subaccountId = subaccountId;
    },
    mergeShipmentPackages(oldObject, newObject) {
      const dimensions = {
        width: {
          disabled: false,
          value: oldObject.dimensions.width.value
        },
        height: {
          disabled: false,
          value: oldObject.dimensions.height.value
        },
        length: {
          disabled: false,
          value: oldObject.dimensions.length.value
        }
      }
      const mergeData = {
        colli: oldObject.colli ? oldObject.colli : 1,
        dimensions: dimensions,
        colliWeight: oldObject.colliWeight,
        content: oldObject.content,
        dangerousGoodsServices: oldObject.dangerousGoodsServices,
        isDgToggleActive: oldObject.isDgToggleActive ? oldObject.isDgToggleActive : false,
        packingType: oldObject.packingType,
        packingTypeOption: oldObject.packingTypeOption,
        allPackedInOne: oldObject.allPackedInOne,
        iataRadioactive: oldObject.iataRadioactive,
        iataLimitations: oldObject.iataLimitations,
        iataAdditionalInfo: oldObject.iataAdditionalInfo,
        qty: oldObject.qty,
        Q: oldObject.Q,
        description: oldObject.description
      }

      return {
        ...newObject,
        ...mergeData
      }
    },
    dgTypeChanged(val) {
      this.dgType = val.dgType;
      this.dgTypeShownOnPackageCustomId = val.packageId;
      //Used to change dg type on all existing packages
      this.shipmentPackages.forEach((pack, packageIndex) => {
        pack.dangerousGoodsServices.forEach((service, serviceIndex) => {
          this.$set(this.shipmentPackages[packageIndex].dangerousGoodsServices[serviceIndex], 'feeWrapperId', this.dgType);
          this.$set(this.shipmentPackages[packageIndex], 'iataLimitations', val.iataLimitations);
          this.$set(this.shipmentPackages[packageIndex], 'iataRadioactive', val.iataRadioactive);
          this.$set(this.shipmentPackages[packageIndex], 'iataAdditionalInfo', val.iataAdditionalInfo);
        });
      });
    },
    packagingTypeChange(index){
      let shipmentPackage = this.shipmentPackages[index]
      shipmentPackage.dangerousGoodsServices.forEach((service, serviceIndex) => {
        this.$set(this.shipmentPackages[index].dangerousGoodsServices[serviceIndex], 'iataLimitations', null);
        this.$set(this.shipmentPackages[index].dangerousGoodsServices[serviceIndex], 'iataRadiation', null);
        this.$set(this.shipmentPackages[index].dangerousGoodsServices[serviceIndex], 'iataAdditionalInfo', null);
      })
    },
    packagingTypeOptionChange(index){
      let shipmentPackage = this.shipmentPackages[index]
      shipmentPackage.dangerousGoodsServices.forEach((service, serviceIndex) => {
        this.$set(this.shipmentPackages[index].dangerousGoodsServices[serviceIndex], 'innerPackaging', null);
        this.$set(this.shipmentPackages[index].dangerousGoodsServices[serviceIndex], 'innerPackagingQty', null);
      })
    },
    resetShippment() {
      if ('Production' === process.env.VUE_APP_MODE) {
        this.$router.go();
      }
      //Object.assign(this.$data, initialState())
    },
    transformColliContent (shipmentPackagesIndex, contents, isObservable = false) {
      //Content should be set only if is seen
      if (!this.showColliContent[shipmentPackagesIndex]) {
        return [];
      }

      return contents.map(content => {
        if (isObservable) {
          return {
            quantity: content.qty,
            unit_weight: content.unitWeight.value,
            unit_weight_unit: content.unitWeight.unit,
          };
        } else {
          return {
            quantity: content.qty,
            description: content.description,
            unit_weight: content.unitWeight.value,
            unit_weight_unit: content.unitWeight.unit,
            unit_value: content.unitValue.value,
            unit_value_currency: content.unitValue.unit,
            hs_code: content.hsCode,
            origin_country_id: content.origin,
          };
        }
      });
    },
    transformColliService (colli, isObservable = false) {
      //If DG toggle is off or user has disabled dangerous goods, do not use DG services
      if (!colli.isDgToggleActive || !this.show_dangerous_goods) {
        return [];
      }

      return colli.dangerousGoodsServices.map(service => {
        //If allPackedInOne option is set to no, then use notAllInOneQty otherwise use colli qty (2 = Not all packed in one)
        let qty = colli.allPackedInOne === 2 ? service.notAllInOneQty : colli.qty;
        //For parcel and load we will not show qty, colli number will be used, only for pallet qty will remain (document goods type does not have dg)
        qty = [1, 4].includes(parseInt(colli.goodsType)) ? colli.colli : qty;
        let packingType = parseInt(colli.allPackedInOne) === 2 ? service.notAllInOneOuterPackaging : colli.packingType;

        if (isObservable) {
          return {
            qty: qty,
            Q: colli.Q,
            fee_wrapper_id: service.feeWrapperId,
            dangerous_goods_id: service.dangerousGoodsId,
            packing_type: colli.packingType,
            packing_type_option: colli.packingTypeOption,
            all_packed_in_one: colli.allPackedInOne,
            iata_limitations: colli.iataLimitations,
            iata_radioactive: colli.iataRadioactive,
            iata_additional_info: colli.iataAdditionalInfo,
            nem_weight: service.nemWeight.value,
            nem_weight_unit: service.nemWeight.unit,
            net_weight: service.netWeight.value,
            net_weight_unit: service.netWeight.unit,
            brutto_weight: service.bruttoWeight.value,
            brutto_weight_unit: service.bruttoWeight.unit,
            is_limited_quantity: service.isLimitedQuantity,
            is_excepted_quantity: service.isExceptedQuantity,
            inner_packaging: service.innerPackagingQty && parseInt(service.innerPackagingQty) > 0 ? service.innerPackaging : '',
          }
        } else {
          return {
            qty: qty,
            Q: colli.Q,
            fee_wrapper_id: service.feeWrapperId,
            dangerous_goods_id: service.dangerousGoodsId,
            packing_type: packingType,
            packing_type_option: colli.packingTypeOption,
            is_overpack: colli.isOverpack,
            all_packed_in_one: colli.allPackedInOne,
            iata_limitations: colli.iataLimitations,
            iata_radioactive: colli.iataRadioactive,
            iata_additional_info: colli.iataAdditionalInfo,
            nem_weight: service.nemWeight.value,
            nem_weight_unit: service.nemWeight.unit,
            net_weight: service.netWeight.value,
            net_weight_unit: service.netWeight.unit,
            brutto_weight: service.bruttoWeight.value,
            brutto_weight_unit: service.bruttoWeight.unit,
            is_environmental_hazard: service.isEnvironmentalHazard,
            is_marine_pollutant: service.isMarinePollutant,
            english_technical_designation: service.englishTechnicalDesignation,
            danish_technical_designation: service.danishTechnicalDesignation,
            iata_autorization: service.iataAutorization,
            flash_point: service.flashPoint,
            segregation_group: service.segregationGroup,
            ems: service.ems,
            inner_packaging: service.innerPackagingQty && parseInt(service.innerPackagingQty) > 0 ? service.innerPackaging : '',
            //inner_packaging: service.innerPackaging,
            inner_packaging_qty: service.innerPackagingQty,
            packing_instruction: service.packingInstruction,
            is_limited_quantity: service.isLimitedQuantity,
            is_excepted_quantity: service.isExceptedQuantity
          }
        }
      });
    },
    setShowColliContent() {
      this.shipmentPackages.forEach((pack,index) => {

        if (parseInt(pack.goodsType) === 2) {//For document goods type, content is never shown
          this.showColliContent[index] = false;
        } else if (this.showCustoms) {//If shipment is customs declarable we need to show content
          this.showColliContent[index] = true
        } else {
          this.showColliContent[index] = this.systemShowColliContent;
        }
      })
    },
    getShowInfoBox() {
      //If shipment is show_booking_info_box true and there is more than 1 active accounts
      if (this.showInfoBox === true && this.get_user.accessible_subaccounts_array.length > 1) {
        return this.showInfoBox;
      }
    },
    closeUnapprovedPopup() {
      this.showUnapprovedPopup = false;
      this.resetShippment()
    },
    // BOOKING SHIPMENT
    bookShipment(productsServicesNotification) {
      let cancelBooking = false;
      let tagError = '';
      this.draftError = null;
      this.custom_tag_json.forEach((tag, index) => {
        // if(tag.custom_tag_value == this.custom_tag_json_tmp[index].custom_tag_value) {
        //   tagError += `${tag.custom_tag_name} ${this.$t('cant be empty')}</br>`
        //   cancelBooking = true;
        // };
        if(tag.custom_tag_is_required && (tag.custom_tag_value == '' || tag.custom_tag_value == this.custom_tag_json_tmp[index].custom_tag_value)) {
          tagError += `${tag.custom_tag_name} ${this.$t('tag required')}</br>`
          cancelBooking = true;
        }
      })
      if(cancelBooking) {
        this.$store.commit('SHOW_NOTIFICATION_BAR', {
          message: tagError,
          type: 'alert-window'
        });
        return;
      };
      if (!this.toData.shipToPrivateResident && !this.toData.company) {
        this.$store.commit('SHOW_NOTIFICATION_BAR', {
          message: 'receiver_company_name_is_required',
          type: 'alert-window'
        });
        return;
      }
      if(this.shipmentPopup){
        if(this.needApproval) {
          this.showUnapprovedPopup = true;
        } else {
          this.isPDFGenerated = false;
          this.showShipmentPopup = true;
        }
      }
      let customs_additional_info_json = {};
      if(this.eori != ''){
        customs_additional_info_json.eori = this.eori;
      }
      if(this.eccn != ''){
        customs_additional_info_json.eori = this.eccn;
      }
      if (this.eeiitn.eei != '' || this.eeiitn.tinType  != '' || this.eeiitn.tinNumber) {
        customs_additional_info_json.eeiitn = {
          eei: this.eeiitn.eei,
          tinType: this.eeiitn.tinType,
          tinNumber: this.eeiitn.tinNumber,
        };
      } else {
        customs_additional_info_json.eeiitn = null;
      }
      this.$store.dispatch('BOOK_SHIPMENT', {
        // GLOBALS
        ship_date: productsServicesNotification.pickup_ship_date ? productsServicesNotification.pickup_ship_date : this.shippingDate,
        subaccount_id: this.bookingSubaccount ? this.bookingSubaccount : this.get_active_subaccount.id,
        // SENDER
        sender: {
          company_name: this.fromData.company,
          name: this.fromData.attention,
          address_1: this.fromAddress.address_1,
          address_2: this.fromAddress.address_2,
          city: this.fromAddress.city,
          post_code: this.fromAddress.post_code,
          country_id: this.fromAddress.country_id,
          phone: this.fromData.phone.number,
          email: this.fromData.email,
          state:  this.fromAddress.state,
          vat: this.fromData.vat ? this.fromAddress.vat : null,
        },
        sender_waybill: {
          company_name: this.waybillData.company,
          name: this.waybillData.attention,
          address_1: this.waybillAddress.address_1,
          address_2: this.waybillAddress.address_2,
          city: this.waybillAddress.city,
          post_code: this.waybillAddress.post_code,
          country_id: this.waybillAddress.country_id,
          phone: this.waybillData.phone.number,
          email: this.waybillData.email,
          state:  this.waybillAddress.state,
        },
        // RECIEVER
        customer: {
          company_name: this.toData.company,
          name: this.toData.attention,
          address_1: this.toAddress.address_1,
          address_2: this.toAddress.address_2,
          city: this.toAddress.city,
          post_code: this.toAddress.post_code,
          country_id: this.toAddress.country_id,
          phone: this.toData.phone.number,
          email: this.toData.email,
          state:  this.toAddress.state,
          vat: this.toData.vat ? this.toData.vat : null,
        },
        // CUSTOMS
        customs: {
          company_name: this.customsData.customsAddressData.company,
          name: this.customsData.customsAddressData.attention,
          address_1: this.customsData.customsAddress.address_1,
          address_2: this.customsData.customsAddress.address_2,
          city: this.customsData.customsAddress.city,
          post_code: this.customsData.customsAddress.post_code,
          country_id: this.customsData.customsAddress.country_id,
          phone: this.customsData.customsAddressData.phone.number,
          email: this.customsData.customsAddressData.email,
          state:  this.customsData.customsAddress.state,
          vat: this.customsData.customsAddressData.vat ? this.customsData.customsAddressData.vat : null,
        },
        customs_additional_info_json: customs_additional_info_json,
        customs_reference: this.customsData.customsAddressData.reference,
        customs_file: this.invoiceFile,
        customs_file_origin: this.isActiveCustom ? '1' : '2',
        goods_incoterm: this.customsData.goods_incoterm, // dodao od gileta
        should_create_customs_file: this.showCustoms ? '1' : '0',
        should_send_customs_file: productsServicesNotification.product.should_send_customs_file,
        invoice_type: this.invoiceType,
        duty_paid_by: this.customsData.duty_paid_by,
        duty_payer_account_no: this.customsData.duty_payer_account_no,
        freight_paid_by: this.customsData.freight_paid_by,
        freight_payer_account_no: this.customsData.freight_payer_account_no,
        freight_payer_id: this.customsData.freight_payer_id,
        freight_booking_confirmation_no: productsServicesNotification.product.freight_booking_confirmation_no,
        pickup_time_from:  productsServicesNotification.pickup_time_from,
        pickup_time_until:  productsServicesNotification.pickup_time_until,
        pickup_driver_note:  productsServicesNotification.pickup_driver_note,
        pickup_action: productsServicesNotification.pickup_action,
        pickup_ship_date:  productsServicesNotification.product.pickup_ship_date,
        // GOODS
        goods_description: '', // posalji prazno
        goods_value_currency: '', // posalji prazno
        goods_value: '', // posalji prazno
        goods_type: this.activeGoodsTypes,
        colli: this.dataForProducts.colli,
        // dodaj delivery za pickup point
        sender_id: this.shipper != null ? this.shipper : '0',
        sender_waybill_address_id: this.waybillShipper != 'custom' ? this.waybillShipper : '0',
        shipping_product_id: productsServicesNotification.product.shipping_product_id, // CHECK
        sender_reference: this.fromData.reference, // CHECK
        receiver_reference: this.toData.reference, // CHECK
        shipment_type: '1', // ZAKUCANO
        //DROPPOINT
        droppoint_no: productsServicesNotification.droppoint_no,
        is_return: this.isReturn,
        fee_wrapper_ids_json: productsServicesNotification.services, // fee_ids_json
        active_services_json: productsServicesNotification.additionalFieldData,
        insurance_amount: '', // vidi sa Danijelom
        insurance_content: '', // vidi sa Danijelom
        carrier_account: '', // posalji prazno
        custom_tags_json: this.custom_tag_json,
        shipment_notification_event: productsServicesNotification.notification.shipmentNotificationType,
        notification_receiver: productsServicesNotification.notification.notification_receiver,
        other_notification_event: productsServicesNotification.notification.other_notification_event,
        transit_time: productsServicesNotification.product.estimated_time.value,

      }).then((res) => {
        if (!res) return;
        if(res.hasOwnProperty('error')){
          this.$store.commit('SHOW_NOTIFICATION_BAR', {
            message: res.error,
            type: 'alert-window',
            iconName: 'check',
          });
          //this.draftError = res.error;
          this.showShipmentPopup = false;
          this.showUnapprovedPopup = false;
          return;
        }
        this.shippingAgentName = res.shipping_agent_name;
        this.shipmentId = res.id;
        this.numPages = this.dataForProducts.colli.length;
        if(this.draftShippmentId != '' && this.get_editing_shipment.shipmentType == 'draft') {
          this.$store.dispatch('DELETE_DRAFTS', {
            type: 'byId',
            id: 0,
            ids: [this.draftShippmentId],
          });
        }
        if(this.draftShippmentId != '' && this.get_editing_shipment.shipmentType == 'unapproved') {
          this.$store.dispatch('DELETE_UNAPPROVEDS', {
            type: 'byId',
            id: 0,
            ids: [this.draftShippmentId],
          });
        }
        // IF NEEDS APPROVAL
        if(this.shipmentPopup) {
          if(!this.needApproval) {
            this.shipmentTraceNo = res.trace_no;
            this.pdfName = `Waybill-${res.trace_no}-${res.shipping_agent_name}-${res.shipping_product_name}-${res.created_at}.pdf`;
            this.$store.dispatch('GET_LABEL', res.id).then((res) => {
              this.$nextTick(() => {
                const urlCreator = window.URL || window.webkitURL;
                let file = urlCreator.createObjectURL(res);
                this.$refs.printPDF.src = file;
                this.$refs.downloadPDF.href = file;
                this.isPDFGenerated = true;
                this.pdfFilePath = file;
              })
            })
          }
        } else {
          this.redirectFromShipment();
        }
      });
    },
    // bookShipment(productsServicesNotification) {
    //   let paramsForBooking = productsServicesNotification;
    //   if(this.draftShippmentId != '') {
    //     this.$store.dispatch('DELETE_DRAFTS', {
    //       type: 'byId',
    //       id: 0,
    //       ids: [this.draftShippmentId],
    //     }).then(() => {
    //       this.bookShipmentWithData(paramsForBooking);
    //     });
    //   } else {
    //     this.bookShipmentWithData(paramsForBooking)
    //   }
    // },
    // PRVA DVA POLJA METODE
    swapData() {
      const fromData = { ...this.fromData };
      const toData = { ...this.toData };
      const fromAddress = { ...this.fromAddress };
      const toAddress = { ...this.toAddress };
      this.fromData = toData;
      this.toData = fromData;
      this.fromAddress = toAddress;
      this.toAddress = fromAddress;

      this.swapped = !this.swapped;
      if(this.swapped){
        this.cachedShipper = this.shipper;
        this.shipper = null;
        this.foreignShipper = true;
        this.isShipperInFrom = false;
      } else {
        this.shipper = this.cachedShipper;
        this.foreignShipper = false;
        this.isShipperInFrom = true;
      }

      this.toWaybill = false;
      this.clearAddressData('waybill');
    },
    changeAddress(type, val) {
      this[`${type}Address`].address_1 = val.address_1 || '';
      this[`${type}Address`].address_2 = val.address_2 || '';
      this[`${type}Address`].country_id = val.country_id || '';
      this[`${type}Address`].post_code = val.post_code || '';
      this[`${type}Address`].city = val.city || '';

      let flag = false;
      stateList.forEach((state) => {
        if(state.country_name ==  this.getCountry(val.country_id, 'name')){
          flag = true;
        }
      });

      if (type === 'to') {
        this[`${type}Data`].shipToPrivateResident = val.customer_type === 2;
      }

      if(flag){
        this[`${type}Address`].state = val.state;
      } else {
        this[`${type}Address`].state = '';
      }
      if(val.typeOfAction == 'app'){
        this[`${type}Data`].company = val.company_name || '';
        this[`${type}Data`].attention = val.name || '';
        this[`${type}Data`].email = val.email || '';
        this[`${type}Data`].phone.number = val.phone || '';
        this[`${type}Data`].vat = val.vat || '';
      }
    },
    clearAddressData(type) {
      this[`${type}Address`].address_1 = '';
      this[`${type}Address`].address_2 = '';
      this[`${type}Address`].country_id = '';
      this[`${type}Address`].post_code = '';
      this[`${type}Address`].city = '';
      this[`${type}Address`].state = '';

      this[`${type}Data`].company = '';
      if (type == 'to') {
        this[`${type}Data`].shipToPrivateResident = false;
      }
      this[`${type}Data`].attention = '';
      this[`${type}Data`].email = '';
      this[`${type}Data`].phone = {
        number: '',
        country: '',
      };

      if (this[`${type}Data`].vat) {
        this[`${type}Data`].vat = '';
      }
    },
    checkForForeign(val) {
      if (val === null) {
        this.foreignShipper = true;
        if(!this.isShipperInFrom){
          this.clearAddressData('from');
        } else {
          this.clearAddressData('from');
          this.cachedShipper = null;
        }
      } else {
        this.foreignShipper = false;
        const data = this.get_shippers.find((opt) => opt.id === val);
        this.fromData = {
          reference: this.fromData.reference,
          company: data.company_name || '',
          attention: data.name,
          email: data.email,
          phone: {'number': data.phone, 'country': countryList.filter((opt) => parseInt(opt.id) === parseInt(data.country_id))[0].alpha2},
        };
        this.fromAddress = {
          address_1: data.address_1,
          address_2: data.address_2,
          city: data.city,
          country_id: data.country_id,
          post_code: data.post_code,
          state: data.state,
        };
      }
    },
    changeWaybillAddressShipper(val) {
      if (val === 'custom') {
        this.clearAddressData('waybill');
      } else {
        const data = this.get_shippers.find((opt) => opt.id === val);
        this.waybillAddress = {
          address_1: data.address_1,
          address_2: data.address_2,
          city: data.city,
          country_id: data.country_id,
          post_code: data.post_code,
          state: data.state,
        };
        this.waybillData = {
          company: data.company_name || '',
          attention: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
        };
      }
    },
    clearFromFields() {
      this.waybillShipper = 'custom';
      this.foreignShipper = false;
      this.shipper = this.get_user.sender_id;
      this.cachedShipper = this.shipper;
      this.clearAddressData('waybill');
      this.clearAddressData('from');
    },
    isMemberOfEU(id) {
      return !!Number(this.getCountry(id, 'member_of_the_eu', '3'));
    },
    showCombinedState(){
      this.showOrigin = true;
      if(this.toAddress.country_id != '' && this.fromAddress.country_id != ''){
        if(  this.isMemberOfEU(this.fromAddress.country_id) && this.isMemberOfEU(this.toAddress.country_id) ){
          this.showOrigin = false;
        } else if( !this.isMemberOfEU(this.fromAddress.country_id) && this.isMemberOfEU(this.toAddress.country_id) ) {
          this.showOrigin = true;
        } else if( !this.isMemberOfEU(this.fromAddress.country_id) && !this.isMemberOfEU(this.toAddress.country_id) ) {
          this.showOrigin = true;
        } else {
          this.showOrigin = true;
        }
      } else {
        if(this.fromAddress.country_id == '') return;
        if(this.isMemberOfEU(this.fromAddress.country_id)){
          this.showOrigin = false;
        }
      }
    },
    // CUSTOMS INFORMATION
    customInfo() {
      this.invoiceFile = null;
      this.invoiceFileName = '';
      this.isActiveCustom = true;
    },
    addFile() {
      this.$refs.invButton.click();
    },
    newFile() {
      this.isActiveCustom = false;
      if(!this.validateInvoiceFile(this.$refs.invButton.files[0])) {
        this.$store.commit('SHOW_NOTIFICATION_BAR', {
          message: 'You can upload only PDF files!',
          type: 'alert-window'
        });
        this.invoiceFileName = null
        return;
      }
      this.convertToBase64(this.$refs.invButton.files[0]);
      this.invoiceFileName = this.$refs.invButton.files[0].name;
    },
    convertToBase64(file) {
        var reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          this.invoiceFile = reader.result.replace('data:application/pdf;base64,','');
        };

    },
    validateInvoiceFile(file) {
      const allowedExtensions = ['pdf'];
      const fileExtension = file.name.split('.').pop().toLowerCase();

      if (!allowedExtensions.includes(fileExtension)) {
        this.$refs.invButton.remove()
        return false;
      }
      return true;
    },
    isInfoValid(type) {
      if(this[`${type}Address`].post_code == undefined || this[`${type}Address`].post_code == null) return false;
      if(this[`${type}Address`].country_id == '' || this[`${type}Address`].country_id == undefined || this[`${type}Address`].country_id == null) return false;
      return true;
    },
    selectAdditionalInfo(val) {
      this.selectedInfoItems = val;
      if(this.selectedInfoItems.indexOf('1') < 0){
        this.eori = '';
      }
      if(this.selectedInfoItems.indexOf('2') < 0){
        this.eccn = '';
      }
    },
    // PAYMENT TERMS - CUSTOMS INFORMATION
    updateIncotermsServices(val) {
      this.incotermServices = val;
    },
    formatUnits(num) {
      let number = num.toString();
      if(number.indexOf(',') > -1){
        number = number.replace(/,/g, '.');
      }
      return Number(number);
    },
    parseColli() {
      let dataArray = [];
      const colliArray = this.shipmentPackages;
      let colliNameNumber = 1;
      for(let ii=0; colliArray.length > ii; ii++){

        const goodsTypeName = colliArray[ii].goodsTypeName;

        if( colliArray[ii].colli > 1){
          for(let kk=0; colliArray[ii].colli > kk; kk++){
            dataArray.push(this.parseColliDataForAPICall(colliArray[ii], `${goodsTypeName} ${colliNameNumber}`, goodsTypeName.toLowerCase(), `Group-${ii}`, ii));
            colliNameNumber++;
          }
        } else {
          dataArray.push(this.parseColliDataForAPICall(colliArray[ii], `${goodsTypeName} ${colliNameNumber}`, goodsTypeName.toLowerCase(), `Group-${ii}`, ii));
          colliNameNumber++;
        }
      }

      return dataArray;
    },
    parseColliDataForAPICall(colli, additionalName, type, group, shipmentPackagesIndex) {
      const showContent = this.showColliContent[shipmentPackagesIndex]
      if(type === 'pallet') {
        return {
          goods_type: colli.goodsType,
          colli_number: additionalName,
          group: group,
          pallet_type: colli.palleteType,
          packaging_name: colli.palleteType,
          description: showContent ? null : colli.description,
          weight: showContent ? null : colli.colliWeight.value,
          weight_type: showContent ? null : colli.colliWeight.unit,
          height: colli.dimensions.height.value,
          length: colli.dimensions.length.value,
          width: colli.dimensions.width.value,
          dimension_type: 'cm',
          pallet_stackable_type: colli.pallet_stackable_type,
          pallet_space: colli.palletSpace,
          isTemperature: colli.isTemperature,
          min_temp: colli.minTemperature,
          max_temp: colli.maxTemperature,
          isDgToggleActive: colli.isDgToggleActive,
          content_json: this.transformColliContent(shipmentPackagesIndex, colli.content),
          services_json: this.transformColliService(colli)
        };
      }
      if(type === 'parcel') {
        return {
          goods_type: colli.goodsType,
          colli_number: additionalName,
          group: group,
          package_id: colli.parcelType,
          packaging_name: colli.packagingName,
          description: showContent ? null : colli.description,
          weight: showContent ? null : colli.colliWeight.value,
          weight_type: showContent ? null : colli.colliWeight.unit,
          height: colli.dimensions.height.value,
          length: colli.dimensions.length.value,
          width: colli.dimensions.width.value,
          dimension_type: colli.dimensions.dimensionsType,
          isTemperature: colli.isTemperature,
          min_temp: colli.minTemperature,
          max_temp: colli.maxTemperature,
          isDgToggleActive: colli.isDgToggleActive,
          content_json: this.transformColliContent(shipmentPackagesIndex, colli.content),
          services_json: this.transformColliService(colli)
        };
      }
      if(type === 'document') {
        let colliWeightValue = colli.colliWeight.value == '' || colli.colliWeight.value == undefined ? 0 : this.formatUnits(colli.colliWeight.value);
        return {
          goods_type: colli.goodsType,
          colli_number: additionalName,
          group: group,
          package_id: colli.parcelType,
          packaging_name: colli.packagingName,
          weight: colliWeightValue,
          weight_type: colli.colliWeight.unit,
          height: colli.dimensions.height.value,
          length: colli.dimensions.length.value,
          width: colli.dimensions.width.value,
          dimension_type: colli.dimensions.dimensionsType,
          services_json: [],
          content_json: [],
        };
      }
      if(type === 'load') {
        return {
          goods_type: colli.goodsType,
          colli_number: additionalName,
          group: group,
          package_id: colli.parcelType,
          packaging_name: colli.packagingName,
          description: showContent ? null : colli.description,
          weight: showContent ? null : colli.colliWeight.value,
          weight_type: showContent ? null : colli.colliWeight.unit,
          height: colli.dimensions.height.value,
          length: colli.dimensions.length.value,
          width: colli.dimensions.width.value,
          dimension_type: colli.dimensions.dimensionsType,
          cubic_meter: colli.cubicMeter,
          load_meter: colli.loadMeter,
          isTemperature: colli.isTemperature,
          min_temp: colli.minTemperature,
          max_temp: colli.maxTemperature,
          isDgToggleActive: colli.isDgToggleActive,
          content_json: this.transformColliContent(shipmentPackagesIndex, colli.content),
          services_json: this.transformColliService(colli)
        };
      }
    },
    parseColliObservable() {
      let dataArray = [];
      const colliArray = this.shipmentPackages;

      let colliNameNumber = 1;
      for(let ii=0; colliArray.length > ii; ii++){

        const goodsTypeName = colliArray[ii].goodsTypeName;

        if( colliArray[ii].colli > 1){
          for(let kk=0; colliArray[ii].colli > kk; kk++){
            dataArray.push(this.parseColliDataForAPICallObservable(colliArray[ii], `${goodsTypeName} ${colliNameNumber}`, goodsTypeName.toLowerCase(), `Group-${ii}`, ii));
            colliNameNumber++;
          }
        } else {
          dataArray.push(this.parseColliDataForAPICallObservable(colliArray[ii], `${goodsTypeName} ${colliNameNumber}`, goodsTypeName.toLowerCase(), `Group-${ii}`, ii));
          colliNameNumber++;
        }
      }

      return dataArray;
    },
    parseColliDataForAPICallObservable(colli, additionalName, type, group, shipmentPackagesIndex) {
      const showContent = this.showColliContent[shipmentPackagesIndex]
      if(type === 'pallet') {
        return {
          goods_type: colli.goodsType,
          colli_number: additionalName,
          group: group,
          pallet_type: colli.palleteType,
          packaging_name: colli.palleteType,
          weight: showContent ? null : colli.colliWeight.value,
          weight_type: showContent ? null : colli.colliWeight.unit,
          height: colli.dimensions.height.value,
          length: colli.dimensions.length.value,
          width: colli.dimensions.width.value,
          dimension_type: 'cm',
          pallet_stackable_type: colli.pallet_stackable_type,
          pallet_space: colli.palletSpace,
          isTemperature: colli.isTemperature,
          min_temp: colli.minTemperature,
          max_temp: colli.maxTemperature,
          isDgToggleActive: colli.isDgToggleActive,
          content_json: this.transformColliContent(shipmentPackagesIndex, colli.content, true),
          services_json: this.transformColliService(colli, true)
        };
      }
      if(type === 'parcel') {
        return {
          goods_type: colli.goodsType,
          colli_number: additionalName,
          group: group,
          package_id: colli.parcelType,
          packaging_name: colli.packagingName,
          weight: showContent ? null : colli.colliWeight.value,
          weight_type: showContent ? null : colli.colliWeight.unit,
          height: colli.dimensions.height.value,
          length: colli.dimensions.length.value,
          width: colli.dimensions.width.value,
          dimension_type: colli.dimensions.dimensionsType,
          isTemperature: colli.isTemperature,
          min_temp: colli.minTemperature,
          max_temp: colli.maxTemperature,
          isDgToggleActive: colli.isDgToggleActive,
          content_json: this.transformColliContent(shipmentPackagesIndex, colli.content, true),
          services_json: this.transformColliService(colli, true)
        };
      }
      if(type === 'document') {
        let colliWeightValue = colli.colliWeight.value == '' || colli.colliWeight.value == undefined ? 0 : this.formatUnits(colli.colliWeight.value);
        return {
          goods_type: colli.goodsType,
          colli_number: additionalName,
          group: group,
          package_id: colli.parcelType,
          packaging_name: colli.packagingName,
          weight: colliWeightValue,
          weight_type: colli.colliWeight.unit,
          height: colli.dimensions.height.value,
          length: colli.dimensions.length.value,
          width: colli.dimensions.width.value,
          dimension_type: colli.dimensions.dimensionsType,
        };
      }
      if(type === 'load') {
        return {
          goods_type: colli.goodsType,
          colli_number: additionalName,
          group: group,
          package_id: colli.parcelType,
          packaging_name: colli.packagingName,
          weight: showContent ? null : colli.colliWeight.value,
          weight_type: showContent ? null : colli.colliWeight.unit,
          height: colli.dimensions.height.value,
          length: colli.dimensions.length.value,
          width: colli.dimensions.width.value,
          dimension_type: colli.dimensions.dimensionsType,
          cubic_meter: colli.cubicMeter,
          load_meter: colli.loadMeter,
          isTemperature: colli.isTemperature,
          min_temp: colli.minTemperature,
          max_temp: colli.maxTemperature,
          isDgToggleActive: colli.isDgToggleActive,
          content_json: this.transformColliContent(shipmentPackagesIndex, colli.content, true),
          services_json: this.transformColliService(colli, true)
        };
      }
    },
    addContentItem(){
      let item;
      if ("goods_type_id" in this.get_user && this.get_user.goods_type_id !== undefined) {
        const userPrefGoodsType = parseInt(this.get_user.goods_type_id)

        if(userPrefGoodsType === 1) {
          item = this.defaultPackageBlocks.parcel;
          item.dangerousGoodsServices[0].feeWrapperId = this.dgType;
          item.parcelType = this.get_user.package_id ?? null
        }
        if(userPrefGoodsType === 2) {
          item = this.defaultPackageBlocks.document
          item.parcelType = this.get_user.package_id ?? null
        }
        if(userPrefGoodsType === 3) {
          item = this.defaultPackageBlocks.pallet
          item.dangerousGoodsServices[0].feeWrapperId = this.dgType;
        }
        if(userPrefGoodsType === 4) {
          item = this.defaultPackageBlocks.load
          item.parcelType = this.get_user.package_id ?? null
          item.dangerousGoodsServices[0].feeWrapperId = this.dgType;
        }
      } else {
        //by default add PARCEL
        item = this.defaultPackageBlocks.parcel;
        item.parcelType = this.get_user.package_id ?? null;
        item.dangerousGoodsServices[0].feeWrapperId = this.dgType;
      }

      let lastPackage = JSON.parse(JSON.stringify(this.shipmentPackages)).pop();
      let packageToCreate = JSON.parse(JSON.stringify(item))

      // If some package already added we want to make a copy of its description
      if (lastPackage && !this.showColliContent[this.shipmentPackages.indexOf(lastPackage)]) {
          packageToCreate.description = lastPackage.description;
      }

      this.shipmentPackages.push(packageToCreate)
      this.updateActiveGoodsTypes();
      this.setShowColliContent();
    },
    updateGoodsType(val, index) {
      if(val === 1) {
        const mergedObject = this.mergeShipmentPackages(this.shipmentPackages[index], this.defaultPackageBlocks.parcel);
        this.$set(this.shipmentPackages, index, mergedObject);
      }
      if(val === 2) {
        const mergedObject = this.mergeShipmentPackages(this.shipmentPackages[index], this.defaultPackageBlocks.document);
        this.$set(this.shipmentPackages, index, mergedObject);
      }
      if(val === 3) {
        const mergedObject = this.mergeShipmentPackages(this.shipmentPackages[index], this.defaultPackageBlocks.pallet);
        this.$set(this.shipmentPackages, index, mergedObject);
      }
      if(val === 4) {
        const mergedObject = this.mergeShipmentPackages(this.shipmentPackages[index], this.defaultPackageBlocks.load);
        this.$set(this.shipmentPackages, index, mergedObject);
      }

      this.updateActiveGoodsTypes();
      this.setShowColliContent();
    },
    updateActiveGoodsTypes()
    {
      const goodsTypes = [];
      this.shipmentPackages.forEach((shipmentPackage) => {
        if (!goodsTypes.includes(shipmentPackage.goodsType)) {
          goodsTypes.push(shipmentPackage.goodsType);
        }
      })
      this.activeGoodsTypes = goodsTypes
    },
    removePackage(index) {
      this.shipmentPackages.splice(index, 1);
      this.updateActiveGoodsTypes();
      this.setShowColliContent();
    },
    // BOOKING
    printShipment() {
      this.$refs.printPDF.contentWindow.print();
    },
    redirectFromShipment() {
      this.$router.push(`/view-shipment/${this.shipmentId}`);
    },
    downloadShipment() {
      this.$refs.downloadPDF.click();
    },
    closeShipmentPopup() {
      this.showShipmentPopup = false;
      this.isPDFGenerated = false;
      this.resetShippment()
    },
    checkCustoms() {
      this.$store.dispatch('GET_CUSTOMS_DECLARABLE', {
        fromCountry: this.fromAddress.country_id,
        fromPostcode: this.fromAddress.post_code,
        toCountry: this.toAddress.country_id,
        toPostcode: this.toAddress.post_code,
        goods_type: JSON.stringify(this.activeGoodsTypes),
      }).then((res) => {
        this.showCustoms = res;
        this.showOrigin = res;
        this.setShowColliContent()
      })
    },
  },
  watch: {
    systemShowColliContent() {
      this.setShowColliContent()
    },
    'activeGoodsTypes': function() {
      if(this.isInfoValid('from') && this.isInfoValid('to')){
        this.checkCustoms();
      }
    },
    'fromAddress.country_id': {
      handler(newVal){
        if(((this.fromData.phone.number && this.fromData.phone.number.length < 5) || !this.fromData.phone.number) && String(newVal) !== ''){
          this.fromData.phone.country = this.getCountry(newVal, 'alpha2');
        }
        if(String(newVal) !== ''){
          // this.showCombinedState();
          if(this.isInfoValid('from') && this.isInfoValid('to')){
            this.checkCustoms();
          }
        }
      },
      deep: true
    },
    'fromAddress.post_code': {
      handler(){
        if(this.isInfoValid('from') && this.isInfoValid('to')){
          this.checkCustoms();
        }
      },
      deep: true
    },
    'toData.shipToPrivateResident': {
      handler(newVal){
        if (newVal === true) {
          this.toData.company = '';
        }
      },
      deep: true
    },
    'waybillAddress.country_id': {
      handler(newVal){
        if(this.waybillData.phone.number.length < 5 && String(newVal) !== ''){
          this.waybillData.phone.country = this.getCountry(newVal, 'alpha2');
        }
      },
      deep: true
    },
    toWaybill: function(val) {
      if(!val) {
        this.waybillShipper = 'custom';
        this.waybillAddress.address_1 = '';
        this.waybillAddress.address_2 = '';
        this.waybillAddress.country_id = '';
        this.waybillAddress.post_code = '';
        this.waybillAddress.city = '';
        this.waybillAddress.state = '';
      }
    },
    infoCustomer: function(val) {
      //if(!val) this.clearInvoiceData();
    },
    'toAddress.country_id': {
      handler(newVal){
        if(this.toData.phone.number.length < 5 && String(newVal) !== ''){
          this.toData.phone.country = this.getCountry(newVal, 'alpha2');
        }
        if(String(newVal) !== ''){
          // this.showCombinedState();
          if(this.isInfoValid('from') && this.isInfoValid('to')){
            this.checkCustoms();
          }
        }
      },
      deep: true
    },
    'toAddress.post_code': {
      handler(){
        if(this.isInfoValid('from') && this.isInfoValid('to')){
          this.checkCustoms();
        }
      },
      deep: true
    },
  },
  mounted() {
    if (this.shipmentPackages.length === 0) {
      this.addContentItem()
    }

    if (!this.get_shipment_editing_mode && this.$store.getters.get_user && this.$store.getters.get_user.receiver_country_id) {
      this.toAddress.country_id = this.$store.getters.get_user.receiver_country_id
    }

    this.$store.dispatch('GET_DANGEROUS_GOODS').then(() => {
      this.dangerousGoodsLoaded = true;
    })
  },
  destroyed() {
    this.$store.dispatch('CLEAR_EDITING_SHIPMENT');
    this.$store.dispatch('CLEAR_PARCEL_TYPES');
  },
};
</script>
